import './style.css';
import ReactDOM from 'react-dom/client';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import { StrictMode, Suspense, useState, useEffect } from 'react';
import './i18n';
import { Leva } from 'leva';
import { useProgress } from '@react-three/drei';
import NavBar from './html/NavBar.jsx';
import Experience from './Experience.jsx';
import LaserChecklist from './html/LaserChecklist.jsx';
import useStore from './stores/useStore.jsx';
import DiscordButton from './html/DiscordButton.jsx';
import ControlsInfo from './html/ControlsInfo.jsx';
import GPUWarning from './html/GPUWarning.jsx';
import TextileChecklist from './html/TextileChecklist.jsx';
import Help from './html/Help.jsx';
import Joystick from './html/Joystick.jsx';
import { registerSW } from 'virtual:pwa-register';

// Register Service Worker for PWA
registerSW({
  onOfflineReady() {
    console.log('PWA is ready to work offline');
  },
  immediate: true
});

const root = ReactDOM.createRoot(document.querySelector('#root'));

// Enable global Three.js caching for FileLoader and other loaders
THREE.Cache.enabled = true;

/**
 * Loader component that displays loading progress.
 * Assets are loaded as needed by the scene components.
 */
export const Loader = ({ onLoaded }) => {
  const { progress, active } = useProgress();

  useEffect(() => {
    // When progress reaches 100% and no more active downloads, signal completion
    if (progress === 100 && !active) {
      console.log('All visible assets loaded');
      // Small delay to ensure everything is really ready
      const timer = setTimeout(() => {
        onLoaded?.();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [progress, active, onLoaded]);

  return (
    <div className="fixed inset-x-0 bottom-0 top-0 z-50 flex flex-col items-center justify-center bg-white">
      <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-black transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="flex flex-col items-center">
        <p className="text-black font-bold font-logo text-xl uppercase tracking-widest">
          {progress < 100 ? `Loading ${progress.toFixed(0)}%` : 'Ready'}
        </p>
      </div>
    </div>
  );
};

// Prevent context menu on right-click for a cleaner WebGL experience
const handleContextMenu = (event) => {
  event.preventDefault();
};

// Show different checklists based on the current scene
export const Checklist = () => {
  const currentScene = useStore((state) => state.currentScene);
  return <>
    {currentScene === 3 && <TextileChecklist />}
    {currentScene === 4 && <LaserChecklist />}
  </>;
};

// Navbar that adapts based on the current scene (e.g., showing a back button in detail view)
export const DynamicNavBar = () => {
  const { currentScene, setCurrentScene } = useStore((state) => ({
    currentScene: state.currentScene,
    setCurrentScene: state.setCurrentScene
  }));

  const handleBackClick = () => {
    setCurrentScene(1);
  };

  return (
    <>
      {currentScene === 4 ? (
        <NavBar
          closeDetailClick={handleBackClick}
          showDetailBar={true}
          detailTitle={'Lasercutter | Speedy 100 Flex'}
        />
      ) : (
        <NavBar />
      )}
    </>
  );
};

// Main Application entry point
export const App = () => {
  const [isWeakGPU, setIsWeakGPU] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [proceedAnyway, setProceedAnyway] = useState(false);
  const [gpuInfo, setGpuInfo] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const currentScene = useStore((state) => state.currentScene);

  // Set background color based on scene
  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement) {
      // scene 4 is LasercutDetailRoom, which should have a white background
      if (currentScene === 4) {
        rootElement.style.background = 'white';
      } else {
        rootElement.style.background = 'black';
      }
    }
  }, [currentScene]);

  // Detect if mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|iphone|ipad|ipod|opera mini|iemobile|mobile/i.test(userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
  }, []);

  // GPU detection logic
  useEffect(() => {
    const gl = document.createElement('canvas').getContext('webgl');
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

    const gpuDetails = {
      vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
      renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
    };

    console.log('Your GPU', gpuDetails);
    setGpuInfo(gpuDetails);

    const isWeakGPU = (gpuDetails) => {
      const weakGPUs = ['Intel HD Graphics', 'Mali-', 'Adreno-', 'PowerVR', 'Apple M1'];
      return weakGPUs.some((name) => gpuDetails.renderer.includes(name));
    };

    setIsWeakGPU(isWeakGPU(gpuDetails));
  }, []);

  const handleProceed = () => {
    setProceedAnyway(true);
  };

  if (isWeakGPU && !proceedAnyway) {
    return <GPUWarning onProceed={handleProceed} gpuInfo={gpuInfo} />;
  }

  return (
    <>
      {/* Loading overlay - shown until all assets are loaded */}
      {!isLoaded && <Loader onLoaded={() => setIsLoaded(true)} />}

      {/* HUD and UI elements */}
      <Leva collapsed hidden={isMobile} />
      <DynamicNavBar />
      <Checklist />
      {isMobile && isLoaded && <Joystick />}

      {/* Show Help modal only after loading is complete */}
      <div className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-[calc(1rem+env(safe-area-inset-right))] flex flex-col-reverse gap-3 pointer-events-none z-60">
        <div className="pointer-events-auto">
          {isLoaded && <Help />}
        </div>
        <div className="pointer-events-auto">
          {isLoaded && <DiscordButton />}
        </div>
      </div>

      {/* Main 3D Canvas container */}
      <Canvas
        shadows
        onContextMenu={handleContextMenu}
        className="r3f"
        gl={{
          antialias: false,
          toneMapping: THREE.ACESFilmicToneMapping,
          outputColorSpace: THREE.SRGBColorSpace,
          pixelRatio: 2
        }}
        camera={{
          layers: 0,
          near: 0.1,
          far: 200,
          fov: 75
        }}>
        <Suspense fallback={null}>
          <Experience />
        </Suspense>
      </Canvas>
      {!isMobile && <ControlsInfo />}
    </>
  );
};

root.render(
  <StrictMode>
    <App />
  </StrictMode>
);
